/* __next_internal_client_entry_do_not_use__ Default,HeroForm,HeroText,HeroCoBrands auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { tv } from 'tailwind-variants';
import { useSitecore } from '@sitecore-content-sdk/nextjs';
import { withStandardComponentWrapper } from 'helpers/HOC';
import ImageWrapper, { normalizeImageUrl } from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { BrandAndThemeProvider } from 'lib/context/BrandAndThemeContext';
import { getTestProps } from 'lib/testing/utils';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import { createCTAClickGTMEvent } from 'lib/utils/gtm-utils';
import GlobalSearchInput from '../search/GlobalSearchInput';
import useDictionary from 'lib/hooks/useDictionary';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
const highlightInTitle = (title, textToHighlight)=>{
    // Escape special characters for RegExp
    const escapedHighlight = textToHighlight?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedHighlight})`, 'gi'); // case-insensitive match
    return {
        value: title?.replace(regex, '<span class="italic text-component-hero-title-emphasis">$1</span>')
    };
};
// Reusable content component for both variants
const HeroContent = ({ title, textToHighlight, description, cta1Link, cta2Link, heroTitle, heroDescription, params, rendering, dynamicPlaceholderId, enableSearch, id, searchPlaceholderText })=>{
    const styles = parseStyleParams(params, [
        'cta1',
        'cta2',
        'cta3'
    ]);
    const phKey = `codeembed-${dynamicPlaceholderId}`;
    const hasCtas = Boolean(cta1Link?.value?.href || cta2Link?.value?.href);
    const { contentContainer, headingText, descriptionText, ctaWrapper, ctaContainer, cta } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsxs("div", {
        className: contentContainer(),
        children: [
            /*#__PURE__*/ _jsx(PlainTextWrapper, {
                className: headingText(),
                field: title?.value ? textToHighlight?.value ? highlightInTitle(title?.value, textToHighlight?.value) : title : title,
                fallbacks: [
                    heroTitle
                ],
                tag: "h1",
                encode: false,
                ...getTestProps(`title`)
            }),
            /*#__PURE__*/ _jsx(RichTextWrapper, {
                className: descriptionText(),
                field: description,
                fallbacks: [
                    heroDescription
                ],
                tag: "div",
                ...getTestProps(`description`)
            }),
            enableSearch?.value && /*#__PURE__*/ _jsx(GlobalSearchInput, {
                placeholder: searchPlaceholderText?.value || 'Search Keywords',
                onSearch: ()=>{
                    console.log('search');
                },
                id: `hero-search-input-${id}`,
                searchType: "banner"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: ctaWrapper(),
                children: [
                    hasCtas && /*#__PURE__*/ _jsxs("div", {
                        className: ctaContainer(),
                        children: [
                            /*#__PURE__*/ _jsx(LinkWrapper, {
                                ctaComponentClass: "component-hero-button-color-1",
                                className: cta({
                                    style: styles.cta1?.ctaVariant
                                }),
                                ctaStyle: getCtaStyle(styles.cta1, 'primary'),
                                field: cta1Link,
                                suppressNewTabIcon: true,
                                ...getTestProps(`link-cta1`),
                                gtmEvent: createCTAClickGTMEvent({
                                    header: title?.value,
                                    ctaText: cta1Link?.value?.text,
                                    ctaType: 'blue',
                                    componentName: 'Core Header',
                                    ctaLink: cta1Link?.value?.href
                                })
                            }),
                            /*#__PURE__*/ _jsx(LinkWrapper, {
                                ctaComponentClass: "component-hero-button-color-2",
                                className: cta({
                                    style: styles.cta2?.ctaVariant
                                }),
                                ctaStyle: getCtaStyle(styles.cta2, 'secondary'),
                                field: cta2Link,
                                suppressNewTabIcon: true,
                                ...getTestProps(`link-cta2`),
                                gtmEvent: createCTAClickGTMEvent({
                                    header: title?.value,
                                    ctaText: cta2Link?.value?.text,
                                    ctaType: 'clear',
                                    componentName: 'Core Header',
                                    ctaLink: cta2Link?.value?.href
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                        name: phKey,
                        rendering: rendering
                    })
                ]
            })
        ]
    });
};
const Hero = (props)=>{
    const { cta1Link, cta2Link, description, title, image, textToHighlight, enableSearch, searchPlaceholderText } = props?.fields || {};
    const { DynamicPlaceholderId } = props?.params || {};
    const { page } = useSitecore();
    const currentPage = page.layout.sitecore.route;
    const { heroTitle, heroDescription, heroImage } = currentPage?.fields ?? {};
    // Extract edit mode from the page context
    const isEditing = page?.mode?.isEditing;
    // Build ImageObject JSON-LD if possible
    const newSrc = normalizeImageUrl(image?.value?.src);
    const isAbsoluteUrl = typeof newSrc === 'string' && /^https?:\/\//i.test(newSrc);
    const imageSchema = isAbsoluteUrl && newSrc ? {
        '@context': 'http://schema.org/',
        '@type': 'ImageObject',
        description: image?.value?.alt,
        image: newSrc
    } : undefined;
    const theme = props?.params?.selectTheme;
    const layout = props?.params?.layout || 'Full'; // Default: Full
    const heroImageLayout = props?.params?.heroImageLayout || 'Full';
    const { base, contentColumn, imageColumnBase, imageColumnRight, imageStyle } = TAILWIND_VARIANTS({
        heroLayout: layout,
        heroImageLayout: heroImageLayout
    });
    return (
        /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
            theme: theme,
            children: /*#__PURE__*/ _jsxs("section", {
                className: base(),
                "data-component": "authorable/shared/content/hero",
                ...editingHydrationProps(isEditing),
                ...getTestProps(`component-hero-${props?.rendering?.uid}`),
                "data-imageurl": image?.value?.src,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: contentColumn(),
                        children: /*#__PURE__*/ _jsx(HeroContent, {
                            title: title,
                            textToHighlight: textToHighlight,
                            description: description,
                            cta1Link: cta1Link,
                            cta2Link: cta2Link,
                            heroTitle: heroTitle,
                            heroDescription: heroDescription,
                            enableSearch: enableSearch,
                            params: props?.params,
                            rendering: props?.rendering,
                            dynamicPlaceholderId: DynamicPlaceholderId,
                            id: props?.rendering?.uid,
                            searchPlaceholderText: searchPlaceholderText
                        })
                    }),
                    image?.value?.src && /*#__PURE__*/ _jsxs("div", {
                        className: `${imageColumnBase()} ${imageColumnRight()}`,
                        children: [
                            /*#__PURE__*/ _jsx(ImageWrapper, {
                                className: imageStyle(),
                                field: image,
                                fallbacks: [
                                    heroImage
                                ],
                                priority: true,
                                fetchPriority: "high",
                                sizes: "(max-width: 768px) 100vw, 50vw",
                                ...getTestProps(`hero-image`)
                            }),
                            !isEditing && imageSchema && /*#__PURE__*/ _jsx("script", {
                                type: "application/ld+json",
                                dangerouslySetInnerHTML: {
                                    __html: JSON.stringify(imageSchema).replace(/</g, '\\u003c')
                                }
                            })
                        ]
                    })
                ]
            })
        })
    );
};
const HeroFormVariant = (props)=>{
    const isEditing = useIsEditing();
    const { cta1Link, cta2Link, description, title, textToHighlight, enableSearch, searchPlaceholderText } = props?.fields || {};
    const { DynamicPlaceholderId } = props?.params || {};
    const { page } = useSitecore();
    const currentPage = page.layout.sitecore.route;
    const { heroTitle, heroDescription } = currentPage?.fields ?? {};
    const theme = props?.params?.selectTheme;
    const layout = props?.params?.layout || 'Full'; // Default: Full
    const heroImageLayout = props?.params?.heroImageLayout || 'Full';
    const phKeyForm = `formcontainer-${DynamicPlaceholderId}`;
    const { base, contentColumn, formContainer } = TAILWIND_VARIANTS({
        heroLayout: layout,
        heroImageLayout: heroImageLayout
    });
    return /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
        theme: theme,
        children: /*#__PURE__*/ _jsxs("section", {
            className: base({
                heroVariant: 'HeroForm'
            }),
            "data-component": "authorable/shared/content/hero-form",
            ...editingHydrationProps(isEditing),
            ...getTestProps(`component-hero-form-${props?.rendering?.uid}`),
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: contentColumn({
                        heroVariant: 'HeroForm'
                    }),
                    children: /*#__PURE__*/ _jsx(HeroContent, {
                        title: title,
                        textToHighlight: textToHighlight,
                        description: description,
                        cta1Link: cta1Link,
                        cta2Link: cta2Link,
                        heroTitle: heroTitle,
                        heroDescription: heroDescription,
                        params: props?.params,
                        rendering: props?.rendering,
                        dynamicPlaceholderId: DynamicPlaceholderId,
                        enableSearch: enableSearch,
                        id: props?.rendering?.uid,
                        searchPlaceholderText: searchPlaceholderText
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: formContainer(),
                    children: /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                        name: phKeyForm,
                        rendering: props?.rendering
                    })
                })
            ]
        })
    });
};
const HeroTextVariant = (props)=>{
    const isEditing = useIsEditing();
    const { cta1Link, cta2Link, description, title, textToHighlight, enableSearch, searchPlaceholderText } = props?.fields || {};
    const { DynamicPlaceholderId } = props?.params || {};
    const { page } = useSitecore();
    const currentPage = page.layout.sitecore.route;
    const { heroTitle, heroDescription } = currentPage?.fields ?? {};
    const theme = props?.params?.selectTheme;
    const layout = props?.params?.layout || 'Full';
    const { base } = TAILWIND_VARIANTS({
        heroLayout: layout
    });
    return /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
        theme: theme,
        children: /*#__PURE__*/ _jsx("section", {
            className: base(),
            "data-component": "authorable/shared/content/hero",
            ...editingHydrationProps(isEditing),
            ...getTestProps(`component-hero-${props?.rendering?.uid}`),
            children: /*#__PURE__*/ _jsx("div", {
                className: "py-component-hero-content-padding-y",
                children: /*#__PURE__*/ _jsx(HeroContent, {
                    title: title,
                    textToHighlight: textToHighlight,
                    description: description,
                    cta1Link: cta1Link,
                    cta2Link: cta2Link,
                    heroTitle: heroTitle,
                    heroDescription: heroDescription,
                    params: props?.params,
                    rendering: props?.rendering,
                    dynamicPlaceholderId: DynamicPlaceholderId,
                    enableSearch: enableSearch,
                    id: props?.rendering?.uid,
                    searchPlaceholderText: searchPlaceholderText
                })
            })
        })
    });
};
const HeroCoBrandsVariant = (props)=>{
    const { title, image, textToHighlight } = props?.fields || {};
    const { page } = useSitecore();
    const currentPage = page.layout.sitecore.route;
    const { heroTitle, heroImage } = currentPage?.fields ?? {};
    const isEditing = page?.mode?.isEditing;
    const { getDictionaryValue } = useDictionary();
    const partnershipText = getDictionaryValue('PartnershipText');
    // Build ImageObject JSON-LD if possible
    const newSrc = normalizeImageUrl(image?.value?.src);
    const isAbsoluteUrl = typeof newSrc === 'string' && /^https?:\/\//i.test(newSrc);
    const imageSchema = isAbsoluteUrl && newSrc ? {
        '@context': 'http://schema.org/',
        '@type': 'ImageObject',
        description: image?.value?.alt,
        image: newSrc
    } : undefined;
    const theme = props?.params?.selectTheme;
    const layout = props?.params?.layout || 'Full'; // Default: Full
    const heroImageLayout = props?.params?.heroImageLayout || 'Full';
    const { base, contentColumn, contentContainer, headingText, imageCoBrandsColumnBase, imageCoBrandsStyle, coBransPartnershipText } = TAILWIND_VARIANTS({
        heroLayout: layout,
        heroImageLayout: heroImageLayout
    });
    return (
        /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
            theme: theme,
            children: /*#__PURE__*/ _jsxs("section", {
                className: base(),
                "data-component": "authorable/shared/content/hero",
                ...editingHydrationProps(isEditing),
                ...getTestProps(`component-hero-${props?.rendering?.uid}`),
                "data-imageurl": image?.value?.src,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: contentColumn(),
                        children: /*#__PURE__*/ _jsx("div", {
                            className: `${contentContainer()} md:py-component-hero-content-padding-y`,
                            children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                className: `${headingText()} !text-left`,
                                field: title?.value ? textToHighlight?.value ? highlightInTitle(title?.value, textToHighlight?.value) : title : title,
                                fallbacks: [
                                    heroTitle
                                ],
                                tag: "h1",
                                encode: false,
                                ...getTestProps(`title`)
                            })
                        })
                    }),
                    image?.value?.src && /*#__PURE__*/ _jsxs("div", {
                        className: imageCoBrandsColumnBase(),
                        children: [
                            /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                className: coBransPartnershipText(),
                                field: {
                                    value: partnershipText
                                },
                                tag: "p",
                                encode: false,
                                ...getTestProps(`partnership-text`)
                            }),
                            /*#__PURE__*/ _jsx(ImageWrapper, {
                                className: imageCoBrandsStyle(),
                                field: image,
                                fallbacks: [
                                    heroImage
                                ],
                                priority: true,
                                fetchPriority: "high",
                                sizes: "(max-width: 768px) 100vw, 50vw",
                                ...getTestProps(`hero-image`)
                            }),
                            !isEditing && imageSchema && /*#__PURE__*/ _jsx("script", {
                                type: "application/ld+json",
                                dangerouslySetInnerHTML: {
                                    __html: JSON.stringify(imageSchema).replace(/</g, '\\u003c')
                                }
                            })
                        ]
                    })
                ]
            })
        })
    );
};
export const Default = withStandardComponentWrapper(Hero);
export const HeroForm = withStandardComponentWrapper(HeroFormVariant);
export const HeroText = withStandardComponentWrapper(HeroTextVariant);
export const HeroCoBrands = withStandardComponentWrapper(HeroCoBrandsVariant);
const TAILWIND_VARIANTS = tv({
    defaultVariants: {
        layout: 'right',
        heroImageLayout: 'Full',
        heroVariant: 'Default'
    },
    slots: {
        base: [
            'flex',
            'md:gap-component-hero-space-between-desktop',
            'gap-component-hero-space-between-mobile',
            'items-center',
            'bg-component-hero-bg'
        ],
        cta: [],
        contentColumn: [
            'flex'
        ],
        imageColumnBase: [
            'w-full',
            'lg:w-1/2',
            'overflow-hidden',
            'aspect-[4/3]'
        ],
        imageCoBrandsColumnBase: [
            'w-full',
            'md:w-1/2',
            'flex',
            'flex-col',
            'gap-spacing-spacing-16'
        ],
        coBransPartnershipText: [
            'text-color-core-color-1-Navy',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium'
        ],
        imageColumnRight: [],
        imageColumnLeft: [],
        contentContainer: [
            'px-0',
            'w-full',
            'flex',
            'flex-col',
            'gap-component-hero-content-spacing-vertical'
        ],
        ctaWrapper: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-24'
        ],
        ctaContainer: [
            'flex',
            'flex-wrap',
            'gap-spacing-spacing-8',
            'md:justify-normal'
        ],
        descriptionText: [
            'text-component-hero-body',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-normal',
            'leading-[150%]',
            'text-center',
            'md:text-left',
            'max-w-full'
        ],
        headingText: [
            'font-normal',
            'text-component-hero-title',
            'font-typography-font-family-header',
            'text-typography-font-size-display',
            'leading-typography-line-height-display',
            'text-center',
            'md:text-left'
        ],
        imageStyle: [
            'w-full',
            'h-full',
            'overflow-hidden'
        ],
        imageCoBrandsStyle: [
            'overflow-hidden',
            'landscape:max-w-[304px]',
            'portrait:max-h-[180px]'
        ],
        formContainer: [
            'w-full',
            'lg:w-1/2',
            'bg-color-grayscale-white',
            'flex',
            'flex-col',
            'gap-general-spacing-gutter',
            'p-padding-base',
            'rounded-general-border-radius-container',
            'shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1)]',
            'lg:flex-1'
        ]
    },
    variants: {
        style: {
            tertiary: {
                cta: [
                    'flex',
                    'items-center',
                    'my-auto',
                    'gap-2',
                    'text-base',
                    // TODO: NEED TO UPDAT FIGMA TOKENS.
                    // This is a temporary fix to make the cta link themeable.
                    'text-component-accordion-toggle-active'
                ]
            },
            tertiaryInverse: {
                cta: [
                    'flex',
                    'items-center',
                    'my-auto',
                    'gap-2',
                    'text-base',
                    'text-component-accordion-toggle-active'
                ]
            },
            primary: {
                cta: [
                    'px-8'
                ]
            },
            secondary: {
                cta: [
                    'px-8'
                ]
            },
            primaryInverse: {
                cta: [
                    'px-8'
                ]
            },
            secondaryInverse: {
                cta: [
                    'px-8'
                ]
            },
            primaryOnCanvas: {
                cta: [
                    'px-8'
                ]
            },
            custom: {
                cta: []
            }
        },
        heroLayout: {
            Full: {
                base: [
                    'py-component-hero-padding-y',
                    'px-component-hero-padding-x',
                    'xl:px-spacing-spacing-80'
                ]
            },
            Wide: {
                base: [
                    'relative',
                    'before:z-[-1]',
                    'before:h-full',
                    'before:absolute',
                    'before:top-0',
                    'before:left-[calc(-50vw+50%)]',
                    'before:bg-component-hero-bg',
                    'before:right-[calc(-50vw+50%)]',
                    'py-component-hero-padding-y',
                    'xl:px-0'
                ]
            }
        },
        heroImageLayout: {
            Full: {
                imageColumnBase: [
                    'rounded-component-hero-image-border-radius',
                    'border',
                    'border-component-hero-image-border',
                    'border-component-hero-image-border-width'
                ],
                imageStyle: [
                    'object-cover',
                    'rounded-component-article-card-image-radius'
                ]
            },
            Contain: {
                imageColumnBase: [
                    'border-0',
                    'rounded-none'
                ],
                imageStyle: [
                    'object-contain',
                    'rounded-none'
                ]
            }
        },
        heroVariant: {
            Default: {
                base: [
                    'lg:flex-row',
                    'flex-col'
                ],
                contentColumn: [
                    'w-full',
                    'lg:w-1/2'
                ]
            },
            HeroForm: {
                base: [
                    'lg:flex-row',
                    'flex-col'
                ],
                contentColumn: [
                    'w-full',
                    'lg:w-1/2'
                ]
            }
        }
    }
});
